import React from 'react';

import { useTheme } from '@reckon-web/theme';
import { Flex } from '@reckon-web/flex';
import { Stack } from '@reckon-web/stack';
import { Text } from '@reckon-web/text';
import { LoadingDots } from '@reckon-web/loading';
import { makeId } from '@reckon-web/utils';
import { ActionButton } from '@reckon-web/button';
import { useToasts } from '@reckon-web/toast';

import { JobWithTasks } from '../../../hooks/use-query-jobs-with-tasks';
import { useUpdateJobStatus } from '../../../hooks/use-update-job-status';
import { DueDate } from '../../common/due-date';
import {
  IDraggable,
  IDroppable,
  Kanban,
  KanbanDraggable,
} from '../../common/kanban';
import { StatusBadge } from '../../common/status-badge';
import {
  CompletedTasks,
  IncompleteTasks,
  OnHoldTasks,
  ReadyForReviewTasks,
  WorkItemTitle,
} from '../helpers';
import { useVisibleProperties } from '../../common/visible-properties';
import { NamedUserAvatar } from '../../common/named-user-avatar';
import { GetJobStatusListResult } from '../../../data/useGetJobStatusList';
import { getVisibleStatusesFromJobStatuses } from '../../../hooks/use-get-visible-job-statuses';
import { JobStatusEnum } from '../../job/types/job-status-enum';

const statusTitleHeight = '42px';

interface DraggableType extends IDraggable {
  job: JobWithTasks;
  locked?: boolean;
  onClick?: () => void;
  onCloseJob: () => void;
  testId?: string;
}

interface DroppableType extends IDroppable<DraggableType> {
  status: GetJobStatusListResult;
  testId?: string;
}

const DraggableCard: React.FC<DraggableType> = ({
  job,
  locked,
  onClick,
  onCloseJob,
  pending,
  testId = `job__kanban-card-${job.cloudId}`,
}) => {
  const theme = useTheme();
  const [{ jobBoard: show }] = useVisibleProperties();

  const backgroundColor = theme.palette.background.base;

  /*
    this is what we wanted to do, teh same as AvatarStack. But getSizes isn't officially exported
    so instead we'll pretend like we got hold of it, but really we'll hardcode the values

    const sizes = getSizes(theme);
    const resolvedSize = sizes[size];
    const maskSize = resolvedSize.stackMask;
  */
  const resolvedSize = {
    boxSize: theme.sizing.xsmall, // 24
    stackMask: 1,
  };
  const avatarOffset = (resolvedSize.boxSize + resolvedSize.stackMask * 2) / 5; // we want to "pull" the next avatar over by 20% of its width

  return (
    <Stack
      gap="small"
      padding="medium"
      borderRadius="small"
      background="base"
      boxShadow="xsmall"
      data={{ testid: testId }}
      css={{
        // FIXME: GAP - do we need a `Card` component?
        ':hover': {
          backgroundColor: theme.palette.background.muted,
        },
      }}
    >
      <Stack gap="xsmall">
        <WorkItemTitle
          name={job.name}
          locked={locked}
          onClick={onClick}
          testId={makeId(testId, 'title')}
        />
        {show.debtor && job.client?.name && (
          <Text
            size="xsmall"
            color="muted"
            data={{ testid: makeId(testId, 'name') }}
          >
            {job.client.name}
          </Text>
        )}
      </Stack>
      {show.dueDate &&
        job.status.status === JobStatusEnum.valuesEnum.Closed &&
        job.closedDate && (
          <DueDate
            prefix="Closed"
            date={job.closedDate}
            size="xsmall"
            testId={makeId(testId, 'closed')}
          />
        )}
      {show.dueDate &&
        job.status.status === JobStatusEnum.valuesEnum.Completed &&
        job.completedDate && (
          <DueDate
            prefix="Completed"
            date={job.completedDate}
            size="xsmall"
            testId={makeId(testId, 'completed')}
          />
        )}
      {show.dueDate &&
        job.status.status !== JobStatusEnum.valuesEnum.Closed &&
        job.status.status !== JobStatusEnum.valuesEnum.Completed &&
        job.dueDate && (
          <DueDate
            prefix="Due"
            date={job.dueDate}
            showDueDays
            size="xsmall"
            testId={makeId(testId, 'due')}
          />
        )}

      {show.tasks &&
        job.tasks &&
        job.status.status === JobStatusEnum.valuesEnum.Closed && (
          <CompletedTasks
            counts={job.taskCountByStatus}
            totalCount={job.tasks.length}
            size="xsmall"
            testId={makeId(testId, 'completed-task')}
          />
        )}
      {show.tasks &&
        job.tasks &&
        job.status.status !== JobStatusEnum.valuesEnum.Closed && (
          <Stack gap="small">
            <IncompleteTasks
              counts={job.taskCountByStatus}
              totalCount={job.tasks.length}
              size="xsmall"
              testId={makeId(testId, 'incomplete-task')}
            />
            <OnHoldTasks
              counts={job.taskCountByStatus}
              size="xsmall"
              testId={makeId(testId, 'on-hold-task')}
            />
            <ReadyForReviewTasks
              counts={job.taskCountByStatus}
              size="xsmall"
              testId={makeId(testId, 'review-task')}
            />
          </Stack>
        )}
      <Flex justifyContent="space-between">
        {/* we want this to take space, even if there is no assignee or they've said not to show assignee. Don't need anything fancy */}
        <div>
          {show.assignee && job.assignee && (
            <NamedUserAvatar
              user={job.assignee}
              avatarSize="xsmall"
              textSize="xsmall"
              suffix="[Assignee]"
              testId={makeId(testId, 'assignee-avatar')}
            />
          )}
        </div>
        <Flex
          alignItems="center"
          css={{
            '> * + *': { marginLeft: -avatarOffset },
            '> *': {
              border: `${resolvedSize.stackMask}px solid ${backgroundColor}`,
              borderRadius: '50%',
            },
            '> *:nth-of-type(1)': {
              zIndex: 2,
            },
            '> *:nth-of-type(2)': {
              zIndex: 1,
            },
          }}
        >
          {show.partner && (
            <NamedUserAvatar
              user={job.partner ?? null}
              hideName
              avatarSize="xsmall"
              textSize="xsmall"
              suffix="[Partner]"
              testId={makeId(testId, 'partner-avatar')}
            />
          )}
          {show.manager && (
            <NamedUserAvatar
              user={job.manager ?? null}
              hideName
              avatarSize="xsmall"
              textSize="xsmall"
              suffix="[Manager]"
              testId={makeId(testId, 'manager-avatar')}
            />
          )}
          {show.staff && (
            <NamedUserAvatar
              user={job.staff ?? null}
              hideName
              avatarSize="xsmall"
              textSize="xsmall"
              suffix="[Staff]"
              testId={makeId(testId, 'staff-avatar')}
            />
          )}
        </Flex>
      </Flex>
      {job.status.status === JobStatusEnum.valuesEnum.Completed && (
        <ActionButton
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onCloseJob();
          }}
          label="Close job"
          weight="bold"
          data-testid={makeId(testId, 'close-job')}
        />
      )}
      {pending && (
        <LoadingDots
          label="pending"
          size="small"
          data-testid={makeId(testId, 'loading-dots')}
        />
      )}
    </Stack>
  );
};

const reducer = (draggable: DraggableType, droppable: DroppableType) => ({
  ...draggable,
  job: {
    ...draggable.job,
    status: droppable.status,
  },
});

const Title = ({
  status,
  draggables,
  testId = `kanban-task-card-title`,
}: DroppableType) => (
  <Flex
    alignItems="center"
    gap="small"
    padding="small"
    justifyContent="space-between"
    height={statusTitleHeight}
  >
    <Flex alignItems="center" gap="xsmall">
      <StatusBadge
        label={status.name}
        colour={status.colour}
        data-testid={makeId(testId, 'status-badge')}
      />
      <Text
        size="xsmall"
        weight="medium"
        color="dim"
        data={{ testid: makeId(testId, 'text') }}
      >
        ({draggables.length})
      </Text>
    </Flex>
  </Flex>
);
// Recommended performance optimisation - memoize to prevent unnecessary re-renders
const MemoisedTitle = React.memo(Title);

const Body: React.FC<DroppableType & { Draggable: KanbanDraggable }> = ({
  id,
  draggables,
  Draggable,
}) => (
  <Flex direction="column" gap="small">
    {draggables.map((draggable, i) => (
      <Draggable
        key={draggable.id}
        draggableId={draggable.id}
        droppableId={id}
        index={i}
        isDragDisabled={draggable.locked}
        onClick={draggable.onClick}
      >
        <DraggableCard {...draggable} />
      </Draggable>
    ))}
  </Flex>
);
// Recommended performance optimisation - memoize to prevent unnecessary re-renders
const MemoisedBody = React.memo(Body);

export const KanbanView: React.FC<{
  jobs: JobWithTasks[];
  jobStatuses: GetJobStatusListResult[];
  compare: (a: JobWithTasks, b: JobWithTasks) => number;
  onClickJob?: (jobId: string) => void;
  testId?: string;
}> = React.memo(
  ({ jobs, jobStatuses, compare, onClickJob, testId = `kanban-view` }) => {
    const [UpdateJobStatusDialog, updateJobStatus] = useUpdateJobStatus();
    const { addToast } = useToasts();
    const closedJobStatus = React.useMemo(
      () =>
        jobStatuses.find((x) => x.status === JobStatusEnum.valuesEnum.Closed),
      [jobStatuses]
    );
    const visibleJobStatuses = React.useMemo(
      () => getVisibleStatusesFromJobStatuses(jobStatuses),
      [jobStatuses]
    );

    const draggables = React.useMemo<DraggableType[]>(() => {
      return jobs.map((job) => ({
        id: job.cloudId || '',
        job,
        locked: job.status.status === JobStatusEnum.valuesEnum.Closed,
        onClick: onClickJob && (() => onClickJob(job.cloudId || '')),
        onCloseJob: async () => {
          const result = await updateJobStatus({
            job,
            status: closedJobStatus!,
          });

          if (result) {
            addToast({
              title: 'Job closed successfully',
              tone: 'positive',
            });
          }
        },
      }));
    }, [jobs, onClickJob, updateJobStatus, addToast, closedJobStatus]);

    const sort = React.useCallback<
      (draggables: DraggableType[]) => DroppableType[]
    >(
      (draggables: DraggableType[]): DroppableType[] =>
        visibleJobStatuses?.map((status) => ({
          id: status.cloudId,
          status,
          draggables: draggables
            .filter(({ job }) => job.status.cloudId === status.cloudId)
            .sort(({ job: a }, { job: b }) => compare(a, b)),
        })) || [],
      [compare, visibleJobStatuses]
    );

    const resolver = React.useCallback(
      async (draggable: DraggableType, droppable: DroppableType) => {
        const { job } = draggable;
        const { status } = droppable;
        return updateJobStatus({ job, status });
      },
      [updateJobStatus]
    );

    return (
      <>
        <Kanban<DraggableType, DroppableType>
          draggables={draggables}
          sort={sort}
          reducer={reducer}
          resolver={resolver}
          Title={MemoisedTitle}
          Body={MemoisedBody}
          columnBgColour="dim"
          testId={makeId(testId, 'kanban')}
        />
        <UpdateJobStatusDialog />
      </>
    );
  }
);
